import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  IconButton,
  Button,
  Tooltip,
  Badge,
  alpha,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  CalendarDays,
  Calendar,
  List,
  Bug,
  Lightbulb,
  Zap,
  HelpCircle,
  FileText,
  Layers,
  Lock,
  ArrowUpRight,
  Repeat,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Task, TaskStatus, TaskPriority, TaskCategory, IssueType } from '@/types';

// Category colors for display
const categoryColors: Record<TaskCategory, string> = {
  development: '#2563eb',
  design: '#8b5cf6',
  testing: '#16a34a',
  documentation: '#0891b2',
  research: '#7c3aed',
  meeting: '#f59e0b',
  review: '#ec4899',
  support: '#14b8a6',
  maintenance: '#6366f1',
  deployment: '#ef4444',
  planning: '#64748b',
  task: '#6b7280',
  subtask: '#94a3b8',
  other: '#9ca3af',
};

// Issue type config
const issueTypeConfig: Record<IssueType, { icon: React.FC<any>; color: string; label: string }> = {
  bug: { icon: Bug, color: '#dc2626', label: 'Bug' },
  feature: { icon: Lightbulb, color: '#16a34a', label: 'Feature' },
  improvement: { icon: Zap, color: '#2563eb', label: 'Improvement' },
  task: { icon: FileText, color: '#6b7280', label: 'Task' },
  question: { icon: HelpCircle, color: '#9333ea', label: 'Question' },
};

type ViewMode = 'month' | 'week';

interface UserCalendarViewProps {
  userId: string;
  userName: string;
}

/**
 * UserCalendarView
 * Calendar view for a specific user's tasks
 * Shows only tasks where user has Responsible or Accountable role
 */
const UserCalendarView: React.FC<UserCalendarViewProps> = ({ userId, userName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [showRecurring, setShowRecurring] = useState(true);

  // Fetch user's tasks
  const { data: tasksResponse } = useQuery({
    queryKey: [...QUERY_KEYS.tasks, 'user', userId],
    queryFn: () => taskService.getUserTasks(userId, 1, 500),
    enabled: !!userId,
  });

  // Filter to R/A tasks only
  const allTasks = useMemo(() => {
    if (!tasksResponse?.items) return [];
    return tasksResponse.items.filter((task) => {
      const raci = task.raci || { responsible: [], accountable: undefined, consulted: [], informed: [] };
      const isResponsible = raci.responsible.includes(userId);
      const isAccountable = raci.accountable === userId;
      return isResponsible || isAccountable;
    });
  }, [tasksResponse, userId]);

  // Create task lookup for quick access to parent tasks
  const taskLookup = useMemo(() => {
    const lookup = new Map<string, Task>();
    allTasks.forEach((task: Task) => lookup.set(task.id, task));
    return lookup;
  }, [allTasks]);

  // Get tasks for a specific date
  const getTasksForDate = (date: Dayjs): Task[] => {
    const filteredTasks = allTasks.filter((task: Task) =>
      dayjs(task.dueDate).isSame(date, 'day')
    );
    
    // Apply recurring filter if needed
    if (!showRecurring) {
      return filteredTasks.filter((task: Task) => !task.isRecurring);
    }
    
    return filteredTasks;
  };

  // Navigation
  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(currentDate.subtract(1, 'month'));
    } else {
      setCurrentDate(currentDate.subtract(1, 'week'));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(currentDate.add(1, 'month'));
    } else {
      setCurrentDate(currentDate.add(1, 'week'));
    }
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks?taskId=${taskId}`);
  };

  // Generate calendar days for month view
  const getMonthDays = () => {
    const startOfMonth = currentDate.startOf('month');
    const daysInMonth = currentDate.daysInMonth();
    const startDay = startOfMonth.day();

    const days: (Dayjs | null)[] = [];

    // Add empty slots for days before month starts
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(startOfMonth.date(i));
    }

    return days;
  };

  // Generate week days
  const getWeekDays = () => {
    const startOfWeek = currentDate.startOf('week');
    const days: Dayjs[] = [];

    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.add(i, 'day'));
    }

    return days;
  };

  // Get priority color
  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.P1:
        return '#ef4444';
      case TaskPriority.P2:
        return '#f59e0b';
      case TaskPriority.P3:
        return '#2563eb';
      case TaskPriority.P4:
        return '#94a3b8';
      default:
        return '#64748b';
    }
  };

  const weekDayNames = isMobile ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Calendar for {userName}
        </Typography>
      </Box>

      {/* Calendar Controls */}
      <Card sx={{ mb: 2, borderRadius: { xs: 2, md: 1 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 1.5, md: 2 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 }, flex: isMobile ? '1 1 100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
            <IconButton onClick={handlePrev} size={isMobile ? 'small' : 'medium'}>
              <ChevronLeft size={isMobile ? 18 : 20} />
            </IconButton>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600} sx={{ textAlign: 'center', minWidth: { xs: 'auto', md: 200 } }}>
              {viewMode === 'month'
                ? currentDate.format(isMobile ? 'MMM YYYY' : 'MMMM YYYY')
                : `${currentDate.startOf('week').format('MMM D')} - ${currentDate.endOf('week').format(isMobile ? 'D' : 'MMM D, YYYY')}`}
            </Typography>
            <IconButton onClick={handleNext} size={isMobile ? 'small' : 'medium'}>
              <ChevronRight size={isMobile ? 18 : 20} />
            </IconButton>
            {!isMobile && (
              <Button variant="outlined" size="small" onClick={handleToday}>
                Today
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'grey.100', borderRadius: 1, p: 0.5 }}>
            <IconButton
              onClick={() => setViewMode('month')}
              size="small"
              sx={{
                p: { xs: 0.75, md: 1 },
                bgcolor: viewMode === 'month' ? 'background.paper' : 'transparent',
                color: viewMode === 'month' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'month' ? 1 : 0,
              }}
            >
              <CalendarDays size={isMobile ? 16 : 18} />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('week')}
              size="small"
              sx={{
                p: { xs: 0.75, md: 1 },
                bgcolor: viewMode === 'week' ? 'background.paper' : 'transparent',
                color: viewMode === 'week' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'week' ? 1 : 0,
              }}
            >
              <List size={isMobile ? 16 : 18} />
            </IconButton>
            {isMobile && (
              <Button size="small" onClick={handleToday} sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.75rem' }}>
                Today
              </Button>
            )}
          </Box>

          {/* Recurring Tasks Filter */}
          {!isMobile && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showRecurring}
                  onChange={(e) => setShowRecurring(e.target.checked)}
                  size="small"
                  icon={<Repeat size={16} style={{ opacity: 0.3 }} />}
                  checkedIcon={<Repeat size={16} />}
                />
              }
              label={<Typography variant="caption">Show recurring</Typography>}
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        {/* Week Day Headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            bgcolor: 'grey.50',
          }}
        >
          {weekDayNames.map((day) => (
            <Box
              key={day}
              sx={{
                p: { xs: 0.75, md: 1.5 },
                textAlign: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary" fontWeight={500}>
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        {viewMode === 'month' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
            }}
          >
            {getMonthDays().map((day, index) => {
              if (!day) {
                return (
                  <Box
                    key={`empty-${index}`}
                    sx={{
                      minHeight: { xs: 52, md: 120 },
                      bgcolor: 'grey.50',
                      borderBottom: '1px solid',
                      borderRight: (index + 1) % 7 !== 0 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  />
                );
              }

              const dayTasks = getTasksForDate(day);
              const isToday = day.isSame(dayjs(), 'day');

              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  sx={{
                    minHeight: { xs: 52, md: 120 },
                    p: { xs: 0.5, md: 1 },
                    borderBottom: '1px solid',
                    borderRight: (index + 1) % 7 !== 0 ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                >
                  {/* Date Number */}
                  <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'space-between', mb: { xs: 0, md: 1 } }}>
                    <Typography
                      variant={isMobile ? 'caption' : 'body2'}
                      fontWeight={isToday ? 700 : 400}
                      sx={{
                        width: { xs: 24, md: 28 },
                        height: { xs: 24, md: 28 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        bgcolor: isToday ? 'primary.main' : 'transparent',
                        color: isToday ? 'white' : 'text.primary',
                      }}
                    >
                      {day.date()}
                    </Typography>
                    {!isMobile && dayTasks.length > 0 && (
                      <Badge badgeContent={dayTasks.length} color="primary" />
                    )}
                  </Box>

                  {/* Mobile: Task dots indicator */}
                  {isMobile && dayTasks.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25, mt: 0.25 }}>
                      {dayTasks.slice(0, 3).map((task, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: getPriorityColor(task.priority),
                          }}
                        />
                      ))}
                      {dayTasks.length > 3 && (
                        <Typography variant="caption" sx={{ fontSize: '0.5rem', lineHeight: 1 }}>+</Typography>
                      )}
                    </Box>
                  )}

                  {/* Desktop: Tasks */}
                  {!isMobile && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {dayTasks.slice(0, 3).map((task) => {
                        const parentTask = task.parentTaskId ? taskLookup.get(task.parentTaskId) : null;
                        const hasSubtasks = (task.subtaskIds?.length || 0) > 0;
                        const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
                        const IssueIcon = task.issueType ? issueTypeConfig[task.issueType]?.icon : null;

                        return (
                          <Tooltip
                            key={task.id}
                            title={
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {task.title}
                                </Typography>
                                {task.isRecurring && (
                                  <Typography variant="caption" display="block" sx={{ color: '#c4b5fd' }}>
                                    🔁 Recurring task
                                  </Typography>
                                )}
                                {task.category && (
                                  <Typography variant="caption" display="block">
                                    Category: {task.category}
                                  </Typography>
                                )}
                                {task.issueType && (
                                  <Typography variant="caption" display="block">
                                    Type: {issueTypeConfig[task.issueType].label}
                                  </Typography>
                                )}
                                {parentTask && (
                                  <Typography variant="caption" display="block">
                                    Subtask of: {parentTask.title}
                                  </Typography>
                                )}
                                {hasSubtasks && (
                                  <Typography variant="caption" display="block">
                                    {task.subtaskIds?.length} subtask(s)
                                  </Typography>
                                )}
                                {isBlocked && (
                                  <Typography variant="caption" display="block" color="warning.main">
                                    Blocked by {task.blockedByTaskIds?.length} task(s)
                                  </Typography>
                                )}
                              </Box>
                            }
                          >
                            <Box
                              onClick={() => handleTaskClick(task.id)}
                              sx={{
                                p: 0.5,
                                borderRadius: 0.5,
                                bgcolor: isBlocked
                                  ? alpha('#f59e0b', 0.2)
                                  : task.isRecurring
                                  ? alpha('#8b5cf6', 0.15)
                                  : `${getPriorityColor(task.priority)}20`,
                                borderLeft: '3px solid',
                                borderColor: isBlocked ? '#f59e0b' : task.isRecurring ? '#8b5cf6' : getPriorityColor(task.priority),
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 0.8,
                                },
                              }}
                            >
                              {/* Icons */}
                              {task.isRecurring && (
                                <Repeat size={10} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                              )}
                              {IssueIcon ? (
                                <IssueIcon
                                  size={10}
                                  style={{ color: issueTypeConfig[task.issueType!].color, flexShrink: 0 }}
                                />
                              ) : hasSubtasks ? (
                                <Layers size={10} style={{ color: '#8b5cf6', flexShrink: 0 }} />
                              ) : null}
                              {isBlocked && <Lock size={10} style={{ color: '#f59e0b', flexShrink: 0 }} />}

                              {/* Title */}
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                  flex: 1,
                                  fontSize: '0.65rem',
                                  textDecoration:
                                    task.status === TaskStatus.COMPLETED
                                      ? 'line-through'
                                      : 'none',
                                }}
                              >
                                {task.title}
                              </Typography>

                              {/* Category badge */}
                              {task.category && (
                                <Box
                                  sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    bgcolor: categoryColors[task.category],
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        );
                      })}
                      {dayTasks.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{dayTasks.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : (
          // Week View
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
            }}
          >
            {getWeekDays().map((day, index) => {
              const dayTasks = getTasksForDate(day);
              const isToday = day.isSame(dayjs(), 'day');

              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  sx={{
                    minHeight: 400,
                    p: 1,
                    borderRight: index < 6 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: isToday ? 'primary.50' : 'transparent',
                  }}
                >
                  {/* Date */}
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography
                      variant="h4"
                      fontWeight={isToday ? 700 : 400}
                      sx={{
                        color: isToday ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {day.date()}
                    </Typography>
                  </Box>

                  {/* Tasks */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {dayTasks.map((task) => {
                      const parentTask = task.parentTaskId ? taskLookup.get(task.parentTaskId) : null;
                      const hasSubtasks = (task.subtaskIds?.length || 0) > 0;
                      const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
                      const IssueIcon = task.issueType ? issueTypeConfig[task.issueType]?.icon : null;

                      return (
                        <Card
                          key={task.id}
                          variant="outlined"
                          onClick={() => handleTaskClick(task.id)}
                          sx={{
                            p: 1.5,
                            borderLeft: '4px solid',
                            borderColor: isBlocked ? '#f59e0b' : task.isRecurring ? '#8b5cf6' : getPriorityColor(task.priority),
                            bgcolor: isBlocked ? alpha('#f59e0b', 0.1) : task.isRecurring ? alpha('#8b5cf6', 0.08) : 'background.paper',
                            cursor: 'pointer',
                            '&:hover': {
                              boxShadow: 2,
                            },
                          }}
                        >
                          {/* Header with issue type icon */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            {task.isRecurring && (
                              <Repeat size={14} style={{ color: '#8b5cf6' }} />
                            )}
                            {IssueIcon && (
                              <IssueIcon
                                size={14}
                                style={{ color: issueTypeConfig[task.issueType!].color }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                flex: 1,
                                textDecoration:
                                  task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                              }}
                            >
                              {task.title}
                            </Typography>
                            {isBlocked && (
                              <Tooltip title="Blocked">
                                <Lock size={14} style={{ color: '#f59e0b' }} />
                              </Tooltip>
                            )}
                          </Box>

                          {/* Parent task link */}
                          {parentTask && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Layers size={12} style={{ color: '#8b5cf6' }} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                Subtask of: {parentTask.title}
                                <ArrowUpRight size={10} />
                              </Typography>
                            </Box>
                          )}

                          {/* Metadata row */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {/* Start Date */}
                            {task.startDate && (
                              <Tooltip title="Start Date">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Calendar size={12} style={{ color: '#94a3b8' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {dayjs(task.startDate).format('MMM D')}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                            
                            {/* Due Date */}
                            <Tooltip title="Due Date">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Clock size={12} style={{ color: '#94a3b8' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {dayjs(task.dueDate).format('MMM D')}
                                </Typography>
                              </Box>
                            </Tooltip>

                            {/* Priority badge */}
                            <Box
                              sx={{
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                bgcolor: `${getPriorityColor(task.priority)}20`,
                                borderLeft: '2px solid',
                                borderColor: getPriorityColor(task.priority),
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={600}
                                sx={{ fontSize: '0.65rem', color: getPriorityColor(task.priority) }}
                              >
                                {task.priority}
                              </Typography>
                            </Box>

                            {/* Status */}
                            <Typography variant="caption" color="text.secondary">
                              {task.status}
                            </Typography>

                            {/* Subtasks indicator */}
                            {hasSubtasks && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Layers size={10} style={{ color: '#8b5cf6' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {task.subtaskIds?.length}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default UserCalendarView;
